import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';

const validateEmail = v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

export default function ForgotPassword() {
  const [email,   setEmail]   = useState('');
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [error,   setError]   = useState('');

  const navigate   = useNavigate();
  const auth       = getAuth();
  const emailError = touched && email && !validateEmail(email) ? 'Enter a valid email address' : '';
  const canSubmit  = validateEmail(email) && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    setError('');
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setSent(true);
    } catch (err) {
      const map = {
        'auth/user-not-found':      'No account found with this email address.',
        'auth/invalid-email':       'That email address doesn\'t look right.',
        'auth/too-many-requests':   'Too many attempts. Please wait a moment and try again.',
      };
      setError(map[err.code] || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const id = 'fp-styles';
    if (document.getElementById(id)) return;
    const el = document.createElement('style');
    el.id = id;
    el.textContent = CSS;
    document.head.appendChild(el);
    return () => el.remove();
  }, []);

  return (
    <div className="fp-root">
      <div className="fp-card">

        {/* back button */}
        <button className="fp-back" onClick={() => navigate('/signin')} aria-label="Back to sign in">
          ← Back to sign in
        </button>

        {sent ? (
          /* ── success state ── */
          <div className="fp-success">
            <div className="fp-success__icon" aria-hidden="true">✉</div>
            <h2 className="fp-success__title">Check your inbox</h2>
            <p className="fp-success__body">
              We've sent a password reset link to <strong>{email}</strong>.
              It may take a minute to arrive — check your spam folder if you don't see it.
            </p>
            <button className="fp-btn-ghost" onClick={() => { setSent(false); setEmail(''); setTouched(false); }}>
              Try a different email
            </button>
            <button className="fp-btn-primary" onClick={() => navigate('/signin')}>
              Back to sign in
            </button>
          </div>
        ) : (
          /* ── form state ── */
          <>
            <div className="fp-header">
              <div className="fp-header__icon" aria-hidden="true">🔑</div>
              <h1 className="fp-title">Reset your password</h1>
              <p className="fp-subtitle">
                Enter the email address on your account and we'll send you a reset link.
              </p>
            </div>

            {error && (
              <div className="fp-alert" role="alert">
                <span className="fp-alert__icon">!</span>
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <div className={`fp-field ${touched && email ? (emailError ? 'fp-field--error' : 'fp-field--ok') : ''}`}>
                <label className="fp-label" htmlFor="fp-email">Email address</label>
                <input
                  id="fp-email"
                  className="fp-input"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  onBlur={() => setTouched(true)}
                  placeholder="you@example.com"
                  disabled={loading}
                  aria-describedby={emailError ? 'fp-email-err' : undefined}
                />
                {emailError && <span id="fp-email-err" className="fp-field-msg">{emailError}</span>}
              </div>

              <button
                type="submit"
                className="fp-btn-primary"
                disabled={!canSubmit}
                aria-busy={loading}
              >
                {loading
                  ? <><span className="fp-spinner" aria-hidden="true" /> Sending…</>
                  : 'Send reset link'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600&display=swap');

.fp-root {
  --cream:    #fdf6ee;
  --terra:    #c0533a;
  --terra-h:  #a8432c;
  --terra-lt: #f8ece8;
  --ink:      #1a1008;
  --muted:    #7a6652;
  --border:   #e8ddd0;
  --red:      #dc2626;
  --green:    #16a34a;
  min-height: 100vh;
  display: flex; align-items: center; justify-content: center;
  background:
    radial-gradient(ellipse 60% 50% at 80% 20%, rgba(192,83,58,.07) 0%, transparent 70%),
    radial-gradient(ellipse 40% 60% at 10% 80%, rgba(184,134,11,.05) 0%, transparent 70%),
    var(--cream);
  font-family: 'DM Sans', sans-serif;
  padding: 2rem 1rem;
}

.fp-card {
  background: #fff; border: 1px solid var(--border); border-radius: 20px;
  padding: 2.25rem 2rem 2.5rem;
  width: 100%; max-width: 420px;
  box-shadow: 0 8px 32px rgba(26,16,8,.08);
  display: flex; flex-direction: column; gap: 1.5rem;
  animation: fp-fadein .35s ease;
}

/* back */
.fp-back {
  background: none; border: none; cursor: pointer;
  color: var(--muted); font-family: inherit; font-size: .875rem;
  padding: .25rem 0; align-self: flex-start;
  transition: color .15s;
}
.fp-back:hover { color: var(--ink); }

/* header */
.fp-header { display: flex; flex-direction: column; align-items: center; text-align: center; gap: .625rem; }
.fp-header__icon {
  width: 52px; height: 52px; border-radius: 50%;
  background: var(--terra-lt); font-size: 1.4rem;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: .25rem;
}
.fp-title {
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.75rem; font-weight: 600; color: var(--ink); margin: 0;
}
.fp-subtitle { color: var(--muted); font-size: .9rem; line-height: 1.6; margin: 0; }

/* alert */
.fp-alert {
  display: flex; align-items: flex-start; gap: .75rem;
  background: #fef2f2; border: 1px solid #fecaca;
  border-radius: 10px; padding: .875rem 1rem;
  color: var(--red); font-size: .875rem; line-height: 1.5;
  animation: fp-fadein .2s ease;
}
.fp-alert__icon {
  width: 18px; height: 18px; border-radius: 50%;
  background: var(--red); color: #fff;
  display: grid; place-items: center;
  font-size: .7rem; font-weight: 700; flex-shrink: 0; margin-top: 1px;
}

/* field */
.fp-field { display: flex; flex-direction: column; gap: .375rem; }
.fp-label { font-size: .82rem; font-weight: 600; color: var(--ink); letter-spacing: .02em; }
.fp-input {
  width: 100%; box-sizing: border-box;
  padding: .7rem .9rem; border: 1.5px solid var(--border); border-radius: 10px;
  font-family: inherit; font-size: .95rem; color: var(--ink);
  background: #fff; outline: none;
  transition: border-color .15s, box-shadow .15s;
}
.fp-input:focus { border-color: var(--terra); box-shadow: 0 0 0 3px rgba(192,83,58,.1); }
.fp-input:disabled { opacity: .55; }
.fp-field--ok    .fp-input:not(:focus) { border-color: var(--green); }
.fp-field--error .fp-input:not(:focus) { border-color: var(--red); }
.fp-field-msg { font-size: .78rem; color: var(--red); }

/* buttons */
.fp-btn-primary {
  width: 100%; padding: .875rem; border-radius: 10px;
  background: var(--terra); color: #fff;
  font-family: inherit; font-size: .95rem; font-weight: 600;
  border: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: .5rem;
  transition: background .15s, transform .1s, box-shadow .15s;
  box-shadow: 0 2px 8px rgba(192,83,58,.25);
  margin-top: .25rem;
}
.fp-btn-primary:hover:not(:disabled) {
  background: var(--terra-h); transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(192,83,58,.35);
}
.fp-btn-primary:disabled { opacity: .5; cursor: not-allowed; transform: none; }

.fp-btn-ghost {
  width: 100%; padding: .75rem; border-radius: 10px;
  background: transparent; color: var(--muted);
  font-family: inherit; font-size: .875rem; font-weight: 500;
  border: 1.5px solid var(--border); cursor: pointer;
  transition: border-color .15s, color .15s, background .15s;
}
.fp-btn-ghost:hover { border-color: var(--terra); color: var(--terra); background: var(--terra-lt); }

/* spinner */
.fp-spinner {
  width: 16px; height: 16px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,.3); border-top-color: #fff;
  animation: fp-spin .7s linear infinite; display: inline-block;
}

/* success */
.fp-success {
  display: flex; flex-direction: column; align-items: center;
  text-align: center; gap: 1rem;
  animation: fp-fadein .35s ease;
}
.fp-success__icon {
  width: 64px; height: 64px; border-radius: 50%;
  background: var(--terra-lt); color: var(--terra);
  font-size: 1.75rem; display: flex; align-items: center; justify-content: center;
  box-shadow: 0 0 0 8px rgba(192,83,58,.08);
  animation: fp-pop .4s cubic-bezier(.34,1.56,.64,1);
  margin-bottom: .25rem;
}
.fp-success__title {
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.75rem; font-weight: 600; color: var(--ink); margin: 0;
}
.fp-success__body {
  font-size: .9rem; color: var(--muted); line-height: 1.65; margin: 0;
}
.fp-success__body strong { color: var(--ink); }

@keyframes fp-fadein { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:none; } }
@keyframes fp-pop    { from { transform:scale(0); } to { transform:scale(1); } }
@keyframes fp-spin   { to { transform:rotate(360deg); } }
`;